/**
 * Appels du back-office — les huit entrées de contrat de DECISIONS §4.2, ni plus ni moins.
 *
 * Toutes les formes de réponse viennent des schémas de `@elemora/shared/api` : le back-office ne
 * redéfinit aucun type de contrat. Les écritures sont **partielles et strictes** : un champ inconnu
 * est refusé par le serveur (`400 VALIDATION_FAILED`), ce qui rend impossible une écriture que le
 * contrat n'autorise pas.
 *
 * Règle A-26 rappelée ici, à l'endroit où elle s'applique : aucune fonction de ce module n'écrit
 * un joueur. Il n'existe que `listerJoueurs`, en lecture.
 */
import {
  adminBalanceResponseSchema,
  adminCardResponseSchema,
  adminCardsResponseSchema,
  adminContentPublishResponseSchema,
  adminLevelResponseSchema,
  adminLevelsResponseSchema,
  adminLoginResponseSchema,
  adminPlayersResponseSchema,
  auditListResponseSchema,
  manifestSchema,
} from '@elemora/shared/api';
import { API_PATHS } from '@elemora/shared/constants';

import { requete } from './http';

import type {
  AdminBalancePatch,
  AdminBalanceResponse,
  AdminCardPatch,
  AdminCardResponse,
  AdminCardsResponse,
  AdminContentPublishResponse,
  AdminLevelPatch,
  AdminLevelResponse,
  AdminLevelsResponse,
  AdminLoginResponse,
  AdminPlayersResponse,
  AuditListResponse,
} from '@elemora/shared/api';

/** Connexion au back-office. `403 FORBIDDEN` si le compte n'est pas administrateur. */
export function connexionAdmin(email: string, password: string): Promise<AdminLoginResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.admin.login,
    schema: adminLoginResponseSchema,
    corps: { email, password },
    authentifiee: false,
  });
}

/** `GET /admin/cards` — cartes complètes du brouillon (ou de la version publiée). */
export function listerCartes(signal?: AbortSignal): Promise<AdminCardsResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.admin.cards,
    schema: adminCardsResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `GET /admin/cards/:id` — `404 NOT_FOUND` si l'identifiant est inconnu. */
export function lireCarte(id: string, signal?: AbortSignal): Promise<AdminCardResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.admin.card(id),
    schema: adminCardResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `PATCH /admin/cards/:id` — écrit **dans le brouillon** uniquement. */
export function modifierCarte(id: string, patch: AdminCardPatch): Promise<AdminCardResponse> {
  return requete({
    methode: 'PATCH',
    chemin: API_PATHS.admin.card(id),
    schema: adminCardResponseSchema,
    corps: patch,
  });
}

/** `GET /admin/balance` — paramètres d'équilibrage en vigueur. */
export function lireBalance(signal?: AbortSignal): Promise<AdminBalanceResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.admin.balance,
    schema: adminBalanceResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `PATCH /admin/balance` — patch partiel strict des clés de `balance.json`. */
export function modifierBalance(patch: AdminBalancePatch): Promise<AdminBalanceResponse> {
  return requete({
    methode: 'PATCH',
    chemin: API_PATHS.admin.balance,
    schema: adminBalanceResponseSchema,
    corps: patch,
  });
}

/** `GET /admin/levels` — résumé des niveaux éditables (40 + `niv_illimite`). */
export function listerNiveaux(signal?: AbortSignal): Promise<AdminLevelsResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.admin.levels,
    schema: adminLevelsResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `GET /admin/levels/:id` — niveau complet (parcours, emplacements, vagues). */
export function lireNiveau(id: string, signal?: AbortSignal): Promise<AdminLevelResponse> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.admin.level(id),
    schema: adminLevelResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** `PATCH /admin/levels/:id` — patch partiel strict, `id` immuable. */
export function modifierNiveau(id: string, patch: AdminLevelPatch): Promise<AdminLevelResponse> {
  return requete({
    methode: 'PATCH',
    chemin: API_PATHS.admin.level(id),
    schema: adminLevelResponseSchema,
    corps: patch,
  });
}

/** `GET /admin/players` — **lecture seule**, données minimisées, `limit` borné à 100. */
export function listerJoueurs(
  parametres: { limit: number; offset: number },
  signal?: AbortSignal,
): Promise<AdminPlayersResponse> {
  const requete_ = new URLSearchParams({
    limit: String(parametres.limit),
    offset: String(parametres.offset),
  });
  return requete({
    methode: 'GET',
    chemin: `${API_PATHS.admin.players}?${requete_.toString()}`,
    schema: adminPlayersResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * `POST /admin/content/publish` — publie le brouillon et **clôt les runs en cours** (§8.5).
 *
 * `versionPubliable` porte la garde d'optimisme : si une autre publication est passée entre-temps,
 * le serveur refuse (`400 VALIDATION_FAILED`) au lieu d'écraser silencieusement.
 */
export function publierContenu(versionPubliable?: string): Promise<AdminContentPublishResponse> {
  return requete({
    methode: 'POST',
    chemin: API_PATHS.admin.contentPublish,
    schema: adminContentPublishResponseSchema,
    corps: versionPubliable === undefined ? {} : { contentVersion: versionPubliable },
  });
}

/** `GET /admin/audit` — journal des écritures administratives, du plus récent au plus ancien. */
export function listerAudit(
  parametres: { limit: number; offset: number },
  signal?: AbortSignal,
): Promise<AuditListResponse> {
  const requete_ = new URLSearchParams({
    limit: String(parametres.limit),
    offset: String(parametres.offset),
  });
  return requete({
    methode: 'GET',
    chemin: `${API_PATHS.admin.audit}?${requete_.toString()}`,
    schema: auditListResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/** Version de contenu **publiée**, lue sur le manifeste servi aux joueurs (`GET /content/manifest`). */
export function lireVersionPubliee(signal?: AbortSignal): Promise<{ hash: string }> {
  return requete({
    methode: 'GET',
    chemin: API_PATHS.content.manifest,
    schema: manifestSchema,
    ...(signal === undefined ? {} : { signal }),
  }).then((manifeste) => ({ hash: manifeste.contentVersion }));
}
